import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, DialogActions, DialogBody, DialogTitle } from "src/components/base/dialog";
import Form from "src/components/base/form";
import { ErrorMessage, Field, FieldGroup, Fieldset, RequiredLabel } from "src/components/base/fieldset";
import { Input } from "src/components/base/input";
import { Button, PrimaryButton } from "src/components/base/button";
import { useForm } from "@tanstack/react-form";
import { SimpleDeviceGroup } from "src/api/generated/frontend_internal_v1";
import { ApiInternal } from "src/api/api_internal";

/**
 * The properties for {@link RenameDeviceGroupDialog}
 */
export type RenameDeviceGroupDialogProps = {
    /** Whether the dialog is currently open */
    isOpen: boolean;
    /** Callback that should be called when closing the popup */
    onClose: () => void;
    /** Callback that should be called when the form has been submitted successfully */
    onSubmit: () => void;
    /** The device group to rename */
    deviceGroup: SimpleDeviceGroup | undefined;
};

/**
 * A dialog to rename a device
 */
export default function RenameDeviceGroupDialog(props: RenameDeviceGroupDialogProps) {
    const [t] = useTranslation("dialog-rename-device-group");
    const [tg] = useTranslation();

    const form = useForm({
        defaultValues: { name: props.deviceGroup?.name ?? "" },
        validators: {
            onSubmitAsync: async ({ value }) => {
                if (!props.deviceGroup) return;
                const res = await ApiInternal.deviceGroups.setName(props.deviceGroup.uuid, value.name);

                if (res.result === "Err") {
                    return {
                        fields: {
                            name: res.error.name_not_unique ? t("error.name-not-unique") : undefined,
                        },
                    };
                }
                props.onSubmit();
            },
        },
    });

    useEffect(() => {
        form.reset();
    }, [props.isOpen, props.deviceGroup]);

    if (!props.deviceGroup) return null;

    return (
        <Dialog open={true} onClose={() => props.onClose()}>
            <DialogTitle>{t("heading.rename-device-group", { old: props.deviceGroup.name })}</DialogTitle>
            <DialogBody>
                <Form onSubmit={form.handleSubmit}>
                    <Fieldset>
                        <FieldGroup>
                            <form.Field name={"name"}>
                                {(fieldApi) => (
                                    <Field>
                                        <RequiredLabel>{t("label.new-name")}</RequiredLabel>
                                        <Input
                                            autoFocus={true}
                                            required={true}
                                            invalid={fieldApi.state.meta.errors.length > 0}
                                            value={fieldApi.state.value}
                                            maxLength={255}
                                            onChange={(e) => fieldApi.handleChange(e.target.value)}
                                        />
                                        {fieldApi.state.meta.errors.map((err) => (
                                            <ErrorMessage>{err}</ErrorMessage>
                                        ))}
                                    </Field>
                                )}
                            </form.Field>
                            <DialogActions>
                                <Button plain={true} onClick={props.onClose}>
                                    {tg("button.cancel")}
                                </Button>
                                <PrimaryButton type={"submit"}>{t("button.rename-device-group")}</PrimaryButton>
                            </DialogActions>
                        </FieldGroup>
                    </Fieldset>
                </Form>
            </DialogBody>
        </Dialog>
    );
}
